import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import Avatar, {
  AVATAR_SIZE_MEDIUM,
  AVATAR_STATUS_ONLINE,
  deriveInitials,
  type AvatarStatus,
} from '../../atoms/Avatar';
import ProfileMenu from '../../molecules/ProfileMenu';
import { Wrapper } from './UserProfile.styled';

/**
 * Caller-supplied identity for the avatar + menu header. Kept loose on purpose
 * — apps already have their own auth/user types, this contract just states
 * what the organism needs to render.
 */
/**
 * Imperative handle exposed to render-prop children. Today only `close` is
 * needed; future additions (e.g. `open`) would be a non-breaking extension.
 */
export interface UserProfileChildrenApi {
  close: () => void;
}

export interface UserProfileUser {
  /** Required: shown in the menu header and used to derive initials when none provided. */
  name: string;
  /** Optional secondary line in the menu header (typically the email). */
  email?: string;
  /** Override the auto-derived initials (e.g. for an org logo). Capped to 2 chars. */
  initials?: string;
  status?: AvatarStatus;
}

export interface UserProfileProps {
  user: UserProfileUser;
  /**
   * Invoked when the user clicks "Log Out" in the menu. Async errors are
   * swallowed — the consumer is expected to surface them through their own
   * auth/UI state. Omit to hide the log-out item entirely.
   */
  onLogout?: () => void | Promise<void>;
  /**
   * Anything you want shown between the header and the log-out footer
   * (typically a notification list, search box, settings shortcuts, etc.).
   *
   * Pass a `ReactNode` for static content, or a render function receiving
   * `{ close }` when a child needs to dismiss the menu (e.g. after the user
   * clicks a notification that navigates elsewhere).
   */
  children?: ReactNode | ((api: UserProfileChildrenApi) => ReactNode);
  size?: number;
  /** Override the menu trigger's aria-label. Defaults to "Account — {name}". */
  triggerAriaLabel?: string;
  dataTestId?: string;
  /** Hide the avatar status dot (defaults to AVATAR_STATUS_ONLINE). */
  hideStatusDot?: boolean;
  /** Custom label for the log-out item, for localisation. */
  logoutLabel?: string;
}

/**
 * Account entry-point shaped like a top-bar profile icon.
 *
 * Renders an Avatar trigger; clicking opens a `ProfileMenu` containing the
 * caller-supplied middle slot (e.g. notifications, links) and an optional
 * Log Out action. Closes on outside click or Escape.
 *
 * This component is **purely presentational** — it has no knowledge of auth,
 * notifications, or routing. App-side wrappers wire those in.
 */
const UserProfile: React.FC<UserProfileProps> = ({
  user,
  onLogout,
  children,
  size = AVATAR_SIZE_MEDIUM,
  triggerAriaLabel,
  dataTestId = 'user-profile',
  hideStatusDot = false,
  logoutLabel,
}) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const close = useCallback(() => setOpen(false), []);
  const toggle = useCallback(() => setOpen((prev) => !prev), []);

  useEffect(() => {
    if (!open) return undefined;

    const onPointer = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (target && wrapperRef.current && !wrapperRef.current.contains(target)) {
        close();
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };

    document.addEventListener('mousedown', onPointer);
    document.addEventListener('touchstart', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('touchstart', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  const handleLogout = useCallback(async () => {
    close();
    if (!onLogout) return;
    try {
      await onLogout();
    } catch {
      // Swallow — the host app owns error surfacing for auth failures.
    }
  }, [onLogout, close]);

  const initials = user.initials || deriveInitials(user.name);
  const meta = user.email && user.email !== user.name ? user.email : undefined;

  return (
    <Wrapper ref={wrapperRef} data-testid={dataTestId}>
      <Avatar
        as="button"
        size={size}
        initials={initials}
        name={user.name}
        status={hideStatusDot ? undefined : (user.status ?? AVATAR_STATUS_ONLINE)}
        isActive={open}
        onClick={toggle}
        ariaHasPopup="menu"
        ariaExpanded={open}
        ariaLabel={triggerAriaLabel}
        dataTestId={`${dataTestId}__avatar`}
      />

      {open && (
        <ProfileMenu
          name={user.name}
          meta={meta}
          dataTestId={`${dataTestId}__menu`}
        >
          {typeof children === 'function' ? children({ close }) : children}
          {onLogout && (
            <ProfileMenu.Footer>
              <ProfileMenu.LogOutItem
                onClick={handleLogout}
                dataTestId={`${dataTestId}__logout`}
                label={logoutLabel}
              />
            </ProfileMenu.Footer>
          )}
        </ProfileMenu>
      )}
    </Wrapper>
  );
};

export default UserProfile;
